import { Center } from "@astryxdesign/core/Center";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Button } from "@astryxdesign/core/Button";
import { Section } from "@astryxdesign/core/Section";
import { Spinner } from "@astryxdesign/core/Spinner";
import { VStack } from "@astryxdesign/core/Stack";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Category, Idea, IdeaStatus } from "../../shared/types";
import type { BoardSearch } from "../router";
import { AppFrame } from "../components/AppFrame";
import { FilterBar } from "../components/FilterBar";
import { IdeaDialog } from "../components/IdeaDialog";
import { IdeaPalette } from "../components/IdeaPalette";
import { IdeaRow } from "../components/IdeaRow";
import { QuickAdd } from "../components/QuickAdd";
import { ApiError } from "../lib/api";
import { matchesQuery, sortIdeas } from "../lib/format";
import { InboxIcon } from "../lib/icons";
import { useMoney } from "../lib/money-queries";
import { useAddIdea, useDeleteWithUndo, useIdeas, usePatchIdea } from "../lib/queries";
import { moveItem, useReorder } from "../lib/useReorder";

export function BoardPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/" });
  const { data: ideas, error, isPending } = useIdeas();
  const addIdea = useAddIdea();
  const patchIdea = usePatchIdea();
  const deleteWithUndo = useDeleteWithUndo();
  // Only for the nav badge: the Money screen owns this data.
  const money = useMoney();
  const [query, setQuery] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = useMemo(() => {
    if (!ideas) return [];
    return sortIdeas(
      ideas.filter(
        (i) =>
          i.status === search.s &&
          (search.c === "all" || i.category === search.c) &&
          matchesQuery(i, query),
      ),
    );
  }, [ideas, search.c, search.s, query]);

  if (error instanceof ApiError && error.status === 401) {
    return <Navigate to="/login" />;
  }

  const full = (prev: Partial<BoardSearch>): BoardSearch => {
    const out: BoardSearch = { c: prev.c ?? "all", s: prev.s ?? "open" };
    if (prev.item !== undefined) out.item = prev.item;
    return out;
  };

  const setFilter = (patch: Partial<{ c: Category | "all"; s: IdeaStatus }>) =>
    void navigate({
      to: "/",
      search: (prev) => ({ ...full(prev), ...patch }),
      replace: true,
    });

  const openIdea = (id: string) =>
    void navigate({ to: "/", search: (prev) => ({ ...full(prev), item: id }) });

  const closeIdea = () =>
    void navigate({
      to: "/",
      search: (prev) => {
        const { c, s } = full(prev);
        return { c, s };
      },
      replace: true,
    });

  const toggleDone = (idea: Idea) =>
    patchIdea.mutate({
      id: idea.id,
      patch: { status: idea.status === "done" ? "open" : "done" },
    });

  // Only pinned rows are hand-orderable; the rest stay newest-first.
  const pinnedCount = filtered.filter((i) => i.pinned).length;
  const reorderPinned = useCallback(
    (from: number, to: number) => {
      const pinned = filtered.filter((i) => i.pinned);
      for (const [index, idea] of moveItem(pinned, from, to).entries()) {
        if (idea.sortOrder !== index) {
          patchIdea.mutate({ id: idea.id, patch: { sortOrder: index } });
        }
      }
    },
    [filtered, patchIdea],
  );
  const { state: dragState, handlersFor } = useReorder({
    count: pinnedCount,
    onDrop: reorderPinned,
  });

  const hasAnyIdeas = (ideas?.length ?? 0) > 0;
  const isFiltered = search.c !== "all" || query !== "";
  const selected = search.item ? (ideas?.find((i) => i.id === search.item) ?? null) : null;

  return (
    <AppFrame active="board" dueCount={money.data?.summary.dueSoonCount ?? 0}>
      <Center axis="horizontal">
        <VStack gap={3} maxWidth={760} width="100%" padding={4}>
          <QuickAdd activeCategory={search.c} onAdd={addIdea} />
          <FilterBar
            category={search.c}
            status={search.s}
            query={query}
            onCategory={(c) => setFilter({ c })}
            onStatus={(s) => setFilter({ s })}
            onQuery={setQuery}
          />
          {isPending ? (
            <Center minHeight={240}>
              <Spinner size="lg" label="Loading" />
            </Center>
          ) : filtered.length === 0 ? (
            <Center minHeight={240}>
              {!hasAnyIdeas ? (
                <EmptyState
                  icon={<InboxIcon fontSize={40} />}
                  title="Nothing here yet"
                  description="Type above to capture your first idea — or just paste a link."
                />
              ) : (
                <EmptyState
                  icon={<InboxIcon fontSize={40} />}
                  title={search.s === "done" && !isFiltered ? "Nothing done yet" : "No matches"}
                  description={
                    search.s === "done" && !isFiltered
                      ? "Finish something and it lands here."
                      : "Try a different filter or search."
                  }
                  actions={
                    isFiltered ? (
                      <Button
                        label="Clear filters"
                        variant="ghost"
                        onClick={() => {
                          setQuery("");
                          setFilter({ c: "all" });
                        }}
                      />
                    ) : undefined
                  }
                />
              )}
            </Center>
          ) : (
            <Section padding={0}>
              <VStack as="ul" gap={0} paddingBlock={1}>
                {filtered.map((idea, index) => (
                  <IdeaRow
                    key={idea.id}
                    idea={idea}
                    onOpen={openIdea}
                    onToggleDone={toggleDone}
                    onDelete={deleteWithUndo}
                    {...(idea.pinned && pinnedCount > 1
                      ? {
                          reorder: handlersFor(index),
                          isDragging: dragState.from === index,
                        }
                      : {})}
                  />
                ))}
              </VStack>
            </Section>
          )}
        </VStack>
      </Center>
      {selected ? <IdeaDialog key={selected.id} idea={selected} onClose={closeIdea} /> : null}
      <IdeaPalette
        ideas={ideas ?? []}
        isOpen={paletteOpen}
        onOpenChange={setPaletteOpen}
        onPick={openIdea}
      />
    </AppFrame>
  );
}
